/**
 * Shared AI offer editor components and utilities.
 * This file exists to break the circular dependency between edit-offer-ai-top-level.tsx and its sub-components.
 */
import React from 'react';
import { type CSSObjectWithLabel } from 'react-select';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { produce } from 'immer';
import { set } from 'lodash';
import { type GenAIGeneratedContent } from '@bbkAdminRedux/rtkq/genai.slice';
import { isLossAversionCard } from '@bbkAdminUtils/api-client/rest-cards-types';
import {
  type NewOffer,
  type RestLossAversion,
  type RestOffer,
} from '@bbkAdminUtils/api-client/rest-offers-api';
import { stingColors } from '@bbkShared/sting-colors';
import { AIIcons } from './ai-icons';

const Loading = styled.div`
  &:after {
    display: inline-block;
    vertical-align: bottom;
    animation: ellipsis steps(1, end) 3s infinite;
    content: '';
    width: 0px;
  }

  @keyframes ellipsis {
    0% {
      content: '';
    }
    25% {
      content: '.';
    }
    50% {
      content: '..';
    }
    75% {
      content: '...';
    }
    100% {
      content: '';
    }
  }
`;

export const AILoading: React.FC = () => {
  return (
    <div
      css={css`
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
      `}
    >
      <div
        css={css`
          background-size: 100%;
          z-index: 100;
          width: 100%;
          height: 100%;
          position: absolute;

          display: flex;
          flex-direction: column;
          justify-content: center;
        `}
      >
        <div className="tw-pb-24 tw-text-center">
          <div className="tw-mb-44 tw-flex tw-justify-center">
            {AIIcons.loadingImage}
          </div>
          <Loading className="tw-mt-4 tw-text-2xl tw-font-semibold tw-text-white">
            Generating offer recommendations
          </Loading>
        </div>
        <div></div>
      </div>
    </div>
  );
};

export const AISideBar = styled.div`
  background: ${stingColors.primary['800']};
  height: 100%;
  padding: 16px 14px;
  overflow-y: hidden;
`;

export const AIDescription = styled.p`
  font-weight: 500;
  color: white;
`;

export const customMenuStyles = {
  container: (provided: CSSObjectWithLabel) => ({
    ...provided,
    border: 'none',
  }),
  control: (provided: CSSObjectWithLabel, state: { isDisabled: boolean }) => ({
    ...provided,
    color: `rgba(255, 255, 255, ${state.isDisabled ? '0.5' : '1'})`,
    background: `rgba(238, 241, 247, 0.25)`,
    border: '1px solid #EEF1F740',
  }),
  singleValue: (
    provided: CSSObjectWithLabel,
    state: { isDisabled: boolean }
  ) => ({
    ...provided,
    color: `rgba(255, 255, 255, ${state.isDisabled ? '0.5' : '1'})`,
  }),
  menu: (provided: CSSObjectWithLabel) => ({
    ...provided,
    zIndex: 9999,
    color: 'black',
  }),
};

export const isAIOffer = (offer: RestOffer | RestLossAversion) => {
  if (!offer?.cards?.length) return false;
  const primaryCard = offer.cards.find(
    (c) => c.name === offer.primary_card_name
  );
  return !!primaryCard?.offer_content_id;
};

export const applyGeneratedContentToOffer = (
  offer: RestOffer | NewOffer,
  content: GenAIGeneratedContent
): RestOffer | NewOffer | undefined => {
  const primaryCardIdx = offer.cards.findIndex(
    (c) => c.name === offer.primary_card_name
  );
  if (primaryCardIdx < 0) return;
  const primaryCard = offer.cards[primaryCardIdx];
  if (!primaryCard) return;

  const {
    title,
    description,
    cta_text: buttonText,
    offer_content_id: contentId,
  } = content;
  const offerContentIdPath = ['cards', primaryCardIdx, 'offer_content_id'];
  const ctaPath = ['cards', primaryCardIdx, 'requires'];

  if (isLossAversionCard(primaryCard)) {
    return produce(offer, (draft) => {
      set(draft, [...ctaPath, 'forwards', 'text'], buttonText);
      set(draft, [...ctaPath, 'title'], title);
      set(draft, [...ctaPath, 'copy'], description);
      set(draft, offerContentIdPath, contentId);
    });
  }

  return produce(offer, (draft) => {
    set(draft, [...ctaPath, 'forwards', 'text'], buttonText);
    set(draft, [...ctaPath, 'info', 'title'], title);
    set(draft, [...ctaPath, 'info', 'details'], description);
    set(draft, offerContentIdPath, contentId);
  });
};
